import { IndianRupee } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Spinner } from "react-bootstrap";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRevenueBreakdown } from "slices/Dashboard/Revenue_Breakdown/thunk";
import { WarehouseOption } from "./DashboardFilters";
import "./TopMetrics.css";

const COLORS = ["#65A19D", "#9B59B6"];

interface RevenueBreakdownItem {
  name: string;
  value: number;
  color: string;
}

interface RevenueBreakdownProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}

export function RevenueBreakdown({ dateFrom, dateTo, warehouse = [] }: RevenueBreakdownProps) {
  const dispatch = useDispatch<any>();
  const { data, loading } = useSelector((state: any) => state.RevenueBreakdownReducer);

  // Fetch revenue breakdown data when filters change
  useEffect(() => {
    dispatch(
      fetchRevenueBreakdown({
        dateFrom,
        dateTo,
        warehouse,
      })
    ).catch((error: any) => {
      console.error("Error fetching revenue breakdown:", error);
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  // Use data from API or default empty data
  const revenueBreakdownData: RevenueBreakdownItem[] = data?.data || [
    { name: "POS Generated", value: 0, color: "#65A19D" },
    { name: "Order Generated", value: 0, color: "#9B59B6" },
  ];
  
  // Check if there's no meaningful data (all values are 0 or no data)
  const hasNoData = !data?.data || revenueBreakdownData.every(item => item.value === 0);
  
  return (
    <div className={`revenue-card position-relative overflow-hidden ${loading ? 'metric-card-loading' : ''}`}>
      <div className="metric-corner-decoration"></div>
      <div className="revenue-header">
        <div className="icon-container icon-container-indigo">
          <IndianRupee className="icon text-indigo" />
        </div>
        <span className="metric-label">Revenue Breakdown (Invoice Source)</span>
      </div>
      
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '180px' }}>
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
      ) : hasNoData ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '180px' }}>
          <span className="text-muted">No data available</span>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={revenueBreakdownData as any}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }:{name?:any,percent?:number}) => percent && `${(percent * 100).toFixed(0)}%`}
                outerRadius={85}
                fill="#8884d8"
                dataKey="value"
              >
                {revenueBreakdownData.map((entry: RevenueBreakdownItem, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => `₹${(value / 100000).toFixed(2)}L`}
              />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="revenue-legend">
            {revenueBreakdownData.map((item: RevenueBreakdownItem, index: number) => (
              <div key={index} className="revenue-legend-item">
                <div className="revenue-legend-content">
                  <div 
                    className="color-dot" 
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <span className="metric-label">{item.name}</span>
                </div>
                <span className="metric-label text-gray-dark">₹{(item.value / 100000).toFixed(2)}L</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

